import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  Loader2,
  Clock,
  Truck,
  ShieldCheck,
  Coins,
  Package2,
  Sun,
  Zap,
  Camera,
  UserCheck,
  Send,
  ArrowRight,
  Store,
  MapPin,
} from "lucide-react";
import { api } from "../lib/api";

/**
 * Phase 100 (10/07/2026) — Landing publique « Devenir hôte distributeur ».
 *
 * Route : /hote-distributeur (publique, pas d'auth).
 * Restriction : Valais uniquement (codes postaux 19xx / 39xx).
 * Objectif : convertir les commerces/entreprises valaisans intéressés en
 * candidatures pour héberger un distributeur automatique Maison Thaï.
 *
 * Confirmation : à la soumission, deux emails Resend partent en parallèle
 * (un vers l'admin, un vers le candidat).
 */

const API = process.env.REACT_APP_BACKEND_URL;

// Codes postaux valaisans : 1868-1999 (Chablais VS + Bas-Valais) + 3900-3999 (Haut-Valais).
const isValaisPostalCode = (pc) => {
  const s = (pc || "").trim();
  if (!/^\d{4}$/.test(s)) return false;
  const n = parseInt(s, 10);
  return (n >= 1868 && n <= 1999) || (n >= 3900 && n <= 3999);
};

export default function DistributorHostRequest() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  // Phase 100 — cache-buster pour la photo distributeur (récupéré via /shop/boutique-section
  // qui expose `uploaded_at`). Sans ça, une nouvelle photo uploadée via le CMS reste
  // masquée par le cache navigateur.
  const [photoVersion, setPhotoVersion] = useState("");
  useEffect(() => {
    fetch(`${API}/api/shop/boutique-section`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const v = d?.host_photo?.uploaded_at;
        if (v) setPhotoVersion(v);
      })
      .catch(() => {});
  }, []);
  const distributeurPhotoUrl = `${API}/api/shop/boutique-section/distributeur-photo/photo.jpg${
    photoVersion ? `?v=${encodeURIComponent(photoVersion)}` : ""
  }`;

  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    business_type: "magasin",
    estimated_daily_visitors: "50-200",
    sun_protection_ok: false,
    power_220v_ok: false,
    camera_authorized: false,
    formation_accepted: false,
    message: "",
  });

  const handleChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const handleCheckbox = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.checked }));

  const allConditionsOk =
    form.sun_protection_ok &&
    form.power_220v_ok &&
    form.camera_authorized &&
    form.formation_accepted;

  const postalCodeError =
    form.postal_code && !isValaisPostalCode(form.postal_code)
      ? "Ce service est réservé au Valais (codes postaux 1868-1999 ou 3900-3999). Nous ne desservons pas les autres cantons."
      : "";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.contact_name || !form.email) {
      toast.error("Merci de remplir les champs obligatoires (entreprise, contact, email)");
      return;
    }
    if (!form.postal_code || !form.city) {
      toast.error("Merci de renseigner votre code postal et votre ville en Valais");
      return;
    }
    if (!isValaisPostalCode(form.postal_code)) {
      toast.error("Service réservé au Valais (1868-1999 / 3900-3999)");
      return;
    }
    setLoading(true);
    try {
      await api.post("/shop/distributor-host-applications", form);
      setSubmitted(true);
      toast.success("Demande envoyée. Un email de confirmation vient de vous être envoyé.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : "Erreur d'envoi. Réessayez ou contactez-nous directement à info@maison-thai.ch.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAF7]">
        <Header />
        <main
          className="max-w-2xl mx-auto px-6 py-16 text-center"
          data-testid="host-success-page"
        >
          <div className="w-16 h-16 mx-auto rounded-full bg-[#E8F2EC] flex items-center justify-center mb-6">
            <CheckCircle2 className="w-8 h-8 text-[#1F4E3D]" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-medium text-[#1A1C1A] mb-4">
            Merci, nous avons bien reçu votre demande 🙏
          </h1>
          <p className="text-[#575A57] leading-relaxed mb-4">
            Un membre de notre équipe vous contactera dans les prochains jours pour organiser
            une visite de votre établissement et évaluer ensemble la meilleure configuration
            de distributeur.
          </p>
          <p className="text-sm text-[#8C8C88] leading-relaxed mb-8">
            📧 Un email de confirmation vient de vous être envoyé à l&apos;adresse indiquée.
            Pensez à vérifier votre dossier spam si vous ne le trouvez pas.
          </p>
          <Link
            to="/boutique"
            className="inline-flex items-center gap-2 bg-[#1F4E3D] text-white px-6 py-3 rounded-full hover:bg-[#173D2F] transition-colors"
            data-testid="host-back-shop-btn"
          >
            <ArrowRight className="w-4 h-4" />
            Retour à la boutique
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="host-request-page">
      <Header />

      {/* HERO — 2 colonnes : texte + photo distributeur */}
      <section className="relative overflow-hidden bg-[#1F4E3D] text-white">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_50%,#C1A063_0%,transparent_50%)]" />
        <div className="max-w-6xl mx-auto px-6 py-14 md:py-20 relative grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-4 py-1.5 text-xs uppercase tracking-wider text-[#A8C9B6] mb-5">
              <Store className="w-3 h-3" /> Partenariat commercial
            </div>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-medium mb-6 leading-tight">
              Aimeriez-vous accueillir un distributeur automatique chez vous&nbsp;?
            </h1>
            <p className="text-lg text-[#A8C9B6] mb-6 leading-relaxed">
              Offrez à vos collaborateurs, clients ou visiteurs des repas thaï artisanaux
              fraîchement préparés — 24&nbsp;h sur 24, sans effort logistique de votre part.
            </p>

            {/* Bandeau restriction Valais — visible, clair, non anxiogène */}
            <div
              className="flex items-start gap-3 bg-[#C1A063]/15 border border-[#C1A063]/40 rounded-lg px-4 py-3 mb-8"
              data-testid="host-valais-only-banner"
            >
              <MapPin className="w-5 h-5 text-[#C1A063] flex-shrink-0 mt-0.5" />
              <div className="text-sm text-[#F4E9D3] leading-relaxed">
                <strong>Service réservé au canton du Valais.</strong>
                <br />
                <span className="text-xs text-[#A8C9B6]">
                  Nous restons volontairement sur notre territoire pour garantir
                  la qualité de service, la fraîcheur et la traçabilité.
                </span>
              </div>
            </div>

            <a
              href="#candidature"
              className="inline-flex items-center gap-2 bg-[#C1A063] hover:bg-[#A98A50] text-[#1F4E3D] font-semibold px-6 py-3 rounded-full transition-colors"
              data-testid="host-cta-hero"
            >
              Je souhaite être contacté <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Photo distributeur — servie via CMS boutique */}
          <div className="relative rounded-xl overflow-hidden shadow-2xl border border-white/10 aspect-[4/5] md:aspect-[4/5] max-w-sm mx-auto md:ml-auto">
            <img
              src={distributeurPhotoUrl}
              alt="Distributeur automatique Maison Thaï"
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback silencieux si le CMS n'a pas encore de photo
                e.currentTarget.style.display = "none";
                e.currentTarget.parentElement.classList.add(
                  "bg-gradient-to-br",
                  "from-[#173D2F]",
                  "to-[#0B241C]"
                );
              }}
              data-testid="host-distributor-photo"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-4 text-xs uppercase tracking-wider text-[#A8C9B6]">
              Distributeur Maison Thaï · en service en Valais
            </div>
          </div>
        </div>
      </section>

      {/* AVANTAGES */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="font-heading text-3xl md:text-4xl text-[#1A1C1A] mb-3 text-center">
          Vos avantages
        </h2>
        <p className="text-center text-[#575A57] mb-10 max-w-2xl mx-auto">
          Un service clé en main : nous fournissons la machine, les produits, la logistique,
          la traçabilité. Vous fournissez simplement l&apos;emplacement.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <Feature
            icon={<Clock className="w-5 h-5 text-[#1F4E3D]" />}
            title="24 / 7 disponibilité"
            text="Vos clients, employés et visiteurs peuvent se restaurer à toute heure, week-ends et jours fériés compris."
          />
          <Feature
            icon={<Truck className="w-5 h-5 text-[#1F4E3D]" />}
            title="Réappro télémétrie"
            text="Réapprovisionnement quotidien ou minimum 3 fois par semaine, piloté par notre système de télémétrie embarquée."
          />
          <Feature
            icon={<ShieldCheck className="w-5 h-5 text-[#1F4E3D]" />}
            title="Traçabilité DLC centralisée"
            text="Contrôle des dates limites de consommation et traçabilité HACCP entièrement gérés chez nous — zéro souci sanitaire pour vous."
          />
          <Feature
            icon={<Coins className="w-5 h-5 text-[#1F4E3D]" />}
            title="Participation progressive"
            text="Vous recevez une commission selon un barème progressif basé sur les ventes générées par votre emplacement."
          />
          <Feature
            icon={<Package2 className="w-5 h-5 text-[#1F4E3D]" />}
            title="Catalogue dédié"
            text="Accès à notre site partenaires avec le détail des barquettes proposées, allergènes, valeurs nutritionnelles et rotations saisonnières."
          />
          <Feature
            icon={<CheckCircle2 className="w-5 h-5 text-[#1F4E3D]" />}
            title="Zéro gestion pour vous"
            text="Pas de commandes, pas de stocks, pas de facturation à gérer. Nous nous occupons de tout — vous restez concentré sur votre métier."
          />
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="bg-[#F4F4F0] border-y border-[#E5E5E0]">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="font-heading text-3xl md:text-4xl text-[#1A1C1A] mb-3 text-center">
            Conditions requises
          </h2>
          <p className="text-center text-[#575A57] mb-10 max-w-2xl mx-auto">
            Pour installer un distributeur dans les meilleures conditions, nous vous demandons de nous assurer&nbsp;:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Condition
              icon={<Sun className="w-5 h-5 text-[#8C6B2F]" />}
              title="Protection soleil"
              text="La machine doit être protégée du soleil direct en permanence (chaleur incompatible avec la conservation)."
            />
            <Condition
              icon={<Zap className="w-5 h-5 text-[#8C6B2F]" />}
              title="Prise 220V"
              text="Une prise électrique 220V standard doit être disponible à proximité immédiate de l'emplacement prévu."
            />
            <Condition
              icon={<Camera className="w-5 h-5 text-[#8C6B2F]" />}
              title="Autorisation caméra"
              text="Possibilité de poser une caméra de surveillance dédiée à la machine (sécurité + monitoring)."
            />
            <Condition
              icon={<UserCheck className="w-5 h-5 text-[#8C6B2F]" />}
              title="Intervention basique"
              text="Durant vos heures d'ouverture, vous acceptez d'intervenir en cas de blocage (rare). Formation de 10 minutes fournie."
            />
          </div>
        </div>
      </section>

      {/* FORMULAIRE */}
      <section id="candidature" className="max-w-3xl mx-auto px-6 py-16">
        <h2 className="font-heading text-3xl md:text-4xl text-[#1A1C1A] mb-3 text-center">
          Inscrivez-vous ci-dessous
        </h2>
        <p className="text-center text-[#575A57] mb-10">
          Remplissez le formulaire — nous vous contactons pour planifier une visite.
        </p>

        <form
          onSubmit={submit}
          className="bg-white border border-[#E5E5E0] rounded-lg p-6 md:p-8 space-y-6"
          data-testid="host-application-form"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <Field
              label="Nom de l'entreprise *"
              placeholder="Ex : Café des Alpes"
              value={form.business_name}
              onChange={handleChange("business_name")}
              required
              testid="host-input-business-name"
            />
            <Field
              label="Personne de contact *"
              placeholder="Prénom Nom"
              value={form.contact_name}
              onChange={handleChange("contact_name")}
              required
              testid="host-input-contact-name"
            />
            <Field
              label="Email *"
              type="email"
              placeholder="vous@entreprise.ch"
              value={form.email}
              onChange={handleChange("email")}
              required
              testid="host-input-email"
            />
            <Field
              label="Téléphone"
              type="tel"
              placeholder="+41 27 555 12 34"
              value={form.phone}
              onChange={handleChange("phone")}
              testid="host-input-phone"
            />
            <div className="md:col-span-2">
              <Field
                label="Adresse"
                placeholder="Rue et numéro"
                value={form.address}
                onChange={handleChange("address")}
                testid="host-input-address"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#575A57] mb-1.5">
                Code postal (Valais requis) *
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                placeholder="1964"
                value={form.postal_code}
                onChange={handleChange("postal_code")}
                required
                className={`w-full px-3 py-2 border rounded text-sm bg-white focus:outline-none ${
                  postalCodeError
                    ? "border-[#C05C47] focus:border-[#C05C47]"
                    : "border-[#E5E5E0] focus:border-[#1F4E3D]"
                }`}
                data-testid="host-input-postal-code"
              />
              {postalCodeError && (
                <p
                  className="text-xs text-[#C05C47] mt-1.5"
                  data-testid="host-postal-code-error"
                >
                  {postalCodeError}
                </p>
              )}
            </div>
            <Field
              label="Ville *"
              placeholder="Sion, Conthey, Brigue…"
              value={form.city}
              onChange={handleChange("city")}
              required
              testid="host-input-city"
            />

            <div>
              <label className="block text-xs uppercase tracking-wider text-[#575A57] mb-1.5">
                Type d&apos;établissement
              </label>
              <select
                value={form.business_type}
                onChange={handleChange("business_type")}
                className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:border-[#1F4E3D]"
                data-testid="host-input-business-type"
              >
                <option value="magasin">Magasin / commerce</option>
                <option value="bureau">Bureau / entreprise</option>
                <option value="garage">Garage / atelier</option>
                <option value="ecole">École / formation</option>
                <option value="sport">Salle de sport / piscine</option>
                <option value="hotel">Hôtel / hébergement</option>
                <option value="hopital">Hôpital / clinique</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#575A57] mb-1.5">
                Trafic quotidien estimé
              </label>
              <select
                value={form.estimated_daily_visitors}
                onChange={handleChange("estimated_daily_visitors")}
                className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:border-[#1F4E3D]"
                data-testid="host-input-visitors"
              >
                <option value="0-50">Moins de 50 personnes/jour</option>
                <option value="50-200">50 à 200 personnes/jour</option>
                <option value="200-500">200 à 500 personnes/jour</option>
                <option value="500+">Plus de 500 personnes/jour</option>
              </select>
            </div>
          </div>

          {/* Conditions cases à cocher */}
          <div className="border-t border-[#E5E5E0] pt-6">
            <div className="text-xs uppercase tracking-wider text-[#575A57] mb-3">
              Je confirme que mon établissement remplit les conditions suivantes *
            </div>
            <div className="space-y-2">
              <CheckboxCondition
                label="La machine sera protégée du soleil en permanence"
                checked={form.sun_protection_ok}
                onChange={handleCheckbox("sun_protection_ok")}
                testid="host-check-sun"
              />
              <CheckboxCondition
                label="Une prise 220V est disponible à proximité"
                checked={form.power_220v_ok}
                onChange={handleCheckbox("power_220v_ok")}
                testid="host-check-power"
              />
              <CheckboxCondition
                label="J'autorise l'installation d'une caméra de surveillance dédiée"
                checked={form.camera_authorized}
                onChange={handleCheckbox("camera_authorized")}
                testid="host-check-camera"
              />
              <CheckboxCondition
                label="J'accepte la formation de 10 min et d'intervenir en cas de blocage rare durant les heures d'ouverture"
                checked={form.formation_accepted}
                onChange={handleCheckbox("formation_accepted")}
                testid="host-check-formation"
              />
            </div>
            {!allConditionsOk && (
              <p className="text-xs text-[#8C6B2F] mt-3">
                💡 Certaines conditions ne sont pas cochées — vous pouvez quand même envoyer votre demande, nous en discuterons lors de la visite.
              </p>
            )}
          </div>

          {/* Message libre */}
          <div>
            <label className="block text-xs uppercase tracking-wider text-[#575A57] mb-1.5">
              Message (optionnel)
            </label>
            <textarea
              value={form.message}
              onChange={handleChange("message")}
              rows={4}
              placeholder="Décrivez brièvement l'emplacement envisagé, votre timing souhaité, vos questions particulières…"
              className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:border-[#1F4E3D] resize-none"
              data-testid="host-input-message"
            />
          </div>

          <button
            type="submit"
            disabled={loading || !!postalCodeError}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1F4E3D] text-white px-6 py-3.5 rounded-full hover:bg-[#173D2F] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="host-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Envoi…
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> Envoyer ma demande
              </>
            )}
          </button>

          <p className="text-xs text-[#8C8C88] text-center leading-relaxed">
            Vos informations restent confidentielles et ne sont utilisées que pour ce projet.
            Aucun partage à des tiers.
          </p>
        </form>
      </section>

      <Footer />
    </div>
  );
}

// ============== Composants internes ==============
function Header() {
  return (
    <header className="bg-white border-b border-[#E5E5E0] sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3">
        <Link
          to="/boutique"
          className="flex items-baseline gap-1.5 shrink-0"
          data-testid="host-header-logo"
        >
          <span className="font-heading text-lg text-[#1F4E3D]">
            Maison Thaï
          </span>
          <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-[#8C8C88] shrink-0">
            · Conthey
          </span>
        </Link>
        <nav className="flex items-center gap-3 sm:gap-6 text-xs sm:text-sm shrink-0">
          <Link to="/boutique" className="text-[#575A57] hover:text-[#1F4E3D]">
            Boutique
          </Link>
          <a
            href="#candidature"
            className="text-[#C1A063] font-semibold hover:underline whitespace-nowrap"
          >
            Devenir hôte
          </a>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A0A0A] text-[#A8C9B6] py-8 text-center text-xs">
      © Maison Thaï · Conthey (Valais) · Cuisine artisanale · Tous droits réservés
    </footer>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 hover:border-[#1F4E3D]/30 transition-colors">
      <div className="w-10 h-10 rounded-md bg-[#E8F2EC] flex items-center justify-center mb-3">
        {icon}
      </div>
      <h3 className="font-heading text-lg text-[#1A1C1A] mb-2">{title}</h3>
      <p className="text-sm text-[#575A57] leading-relaxed">{text}</p>
    </div>
  );
}

function Condition({ icon, title, text }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded-lg p-5 flex gap-4">
      <div className="w-10 h-10 flex-shrink-0 rounded-md bg-[#FBF4E9] flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h3 className="font-heading text-base text-[#1A1C1A] mb-1">{title}</h3>
        <p className="text-sm text-[#575A57] leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function CheckboxCondition({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 p-2 rounded hover:bg-[#FAFAF7] cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-1 w-4 h-4 accent-[#1F4E3D]"
        data-testid={testid}
      />
      <span className="text-sm text-[#1A1C1A] leading-snug">{label}</span>
    </label>
  );
}

function Field({ label, type = "text", placeholder, value, onChange, required, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-[#575A57] mb-1.5">
        {label}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm bg-white focus:outline-none focus:border-[#1F4E3D]"
        data-testid={testid}
      />
    </div>
  );
}
